import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

/* ── nav data ── */
const servicesMegaMenu = {
  label: 'Services',
  items: [
    { label: 'Services Overview', to: '/services', icon: 'ri-briefcase-line', desc: 'Software dev & talent solutions' },
    { label: 'Software Development', to: '/software-development', icon: 'ri-code-box-line', desc: 'End-to-end product engineering' },
    { label: 'Talent Solutions', to: '/talent-solutions', icon: 'ri-user-star-line', desc: 'Pre-vetted engineers on demand' },
    { label: 'Dedicated Dev Team', to: '/dedicated-development-team', icon: 'ri-team-line', desc: 'Your own embedded squad' },
    { label: 'Offshore Team — India', to: '/offshore-development-team-india', icon: 'ri-earth-line', desc: 'Scale with top India talent' },
    { label: 'Hire React Developers', to: '/hire-react-developers', icon: 'ri-reactjs-line', desc: 'Senior React & frontend engineers' },
    { label: 'Hire Python Developers', to: '/hire-python-developers', icon: 'ri-code-s-slash-line', desc: 'Backend, AI & data engineers' },
    { label: 'Hire Node.js Developers', to: '/hire-nodejs-developers', icon: 'ri-server-line', desc: 'Scalable API & full-stack talent' },
  ],
};

const techMegaMenu = {
  label: 'Technologies',
  items: [
    { label: 'Technology Stack', to: '/technologies', icon: 'ri-stack-line', desc: '20+ technologies & rates' },
    { label: 'Find Talent', to: '/talent', icon: 'ri-user-search-line', desc: 'Browse vetted engineers' },
    { label: 'Talent Matching', to: '/how-it-works', icon: 'ri-dashboard-line', desc: 'Our matching process' },
  ],
};

const industriesMegaMenu = {
  label: 'Industries',
  items: [
    { label: 'Industries Overview', to: '/industries', icon: 'ri-building-2-line', desc: 'Sectors we serve & domain expertise' },
    { label: 'E-commerce', to: '/industries/ecommerce', icon: 'ri-shopping-cart-line', desc: 'Retail & omnichannel platform devs' },
    { label: 'EdTech', to: '/industries/edtech', icon: 'ri-graduation-cap-line', desc: 'Learning platform & LMS engineers' },
    { label: 'Fintech', to: '/industries/fintech', icon: 'ri-bank-line', desc: 'Banking & payment systems talent' },
    { label: 'Healthtech', to: '/industries/healthtech', icon: 'ri-hospital-line', desc: 'HIPAA-compliant healthcare devs' },
    { label: 'Logistics', to: '/industries/logistics', icon: 'ri-truck-line', desc: 'Supply chain & route optimization' },
    { label: 'SaaS', to: '/industries/saas', icon: 'ri-apps-line', desc: 'Multi-tenant platform engineers' },
  ],
};

const companyMenu = {
  label: 'Company',
  items: [
    { label: 'About Us', to: '/about', icon: 'ri-information-line', desc: 'Our story, mission & values' },
    { label: 'Case Studies', to: '/case-studies', icon: 'ri-folder-chart-line', desc: 'Client success stories by sector' },
    { label: 'Leadership', to: '/leadership', icon: 'ri-user-star-line', desc: 'Meet the team behind Thoughtic' },
    { label: 'How It Works', to: '/how-it-works', icon: 'ri-route-line', desc: 'From request to deployed team' },
    { label: 'Blog', to: '/blog', icon: 'ri-article-line', desc: 'Insights on hiring & tech' },
    { label: 'Trust & Security', to: '/trust-security', icon: 'ri-shield-check-line', desc: 'Compliance, NDAs & data safety' },
  ],
};

const directLinks = [
  // { label: 'Pricing', to: '/pricing' },
  { label: 'Careers', to: '/careers' },
  { label: 'FAQ', to: '/faq' },
];

export default function SiteNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const megaMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveMegaMenu(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  /* Close mega-menu on outside click */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target as Node)) {
        setActiveMegaMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleHomeCTA = () => {
    if (isHome) {
      const el = document.getElementById('cta');
      if (el) {
        const offset = 80;
        const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: pos, behavior: 'smooth' });
      }
    } else {
      window.REACT_APP_NAVIGATE?.('/#cta');
      setTimeout(() => {
        const el = document.getElementById('cta');
        if (el) {
          const offset = 80;
          const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
          window.scrollTo({ top: pos, behavior: 'smooth' });
        }
      }, 300);
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const isAnyActive = (paths: string[]) => paths.some((p) => isActive(p));

  const navParents = [
    {
      key: 'services',
      label: servicesMegaMenu.label,
      menu: servicesMegaMenu,
      activePaths: servicesMegaMenu.items.map((i) => i.to),
      accent: 'primary',
      menuWidth: 'w-[520px]',
      gridCols: 'grid-cols-2',
    },
    {
      key: 'tech',
      label: techMegaMenu.label,
      menu: techMegaMenu,
      activePaths: techMegaMenu.items.map((i) => i.to),
      accent: 'emerald',
      menuWidth: 'w-[420px]',
      gridCols: 'grid-cols-1',
    },
    {
      key: 'industries',
      label: industriesMegaMenu.label,
      menu: industriesMegaMenu,
      activePaths: industriesMegaMenu.items.map((i) => i.to),
      accent: 'amber',
      menuWidth: 'w-[420px]',
      gridCols: 'grid-cols-1',
    },
    {
      key: 'company',
      label: companyMenu.label,
      menu: companyMenu,
      activePaths: companyMenu.items.map((i) => i.to),
      accent: 'amber',
      menuWidth: 'w-[420px]',
      gridCols: 'grid-cols-1',
    },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHome
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center cursor-pointer group flex-shrink-0"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img
                src="/thoughtic-solutions.png"
                alt="Thoughtic Solutions"
                className="h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center space-x-1" ref={megaMenuRef}>
              {/* Parent menus */}
              {navParents.map((parent) => (
                <div className="relative" key={parent.key}>
                  <button
                    onClick={() => setActiveMegaMenu(activeMegaMenu === parent.key ? null : parent.key)}
                    onMouseEnter={() => setActiveMegaMenu(parent.key)}
                    className={`relative px-3 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-lg ${
                      isAnyActive(parent.activePaths)
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-navy-900 hover:text-primary-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      {parent.label}
                      <i className={`ri-arrow-down-s-line transition-transform duration-200 ${activeMegaMenu === parent.key ? 'rotate-180' : ''}`}></i>
                    </span>
                  </button>

                  <AnimatePresence>
                    {activeMegaMenu === parent.key && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.97 }}
                        transition={{ duration: 0.2 }}
                        onMouseLeave={() => setActiveMegaMenu(null)}
                        className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 ${parent.menuWidth} bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50`}
                      >
                        <div className="p-5">
                          <div className="flex items-center gap-2 mb-4 px-1">
                            <div className={`w-8 h-8 flex items-center justify-center ${
                              parent.accent === 'emerald' ? 'bg-emerald-500' : parent.accent === 'amber' ? 'bg-amber-500' : 'bg-primary-500'
                            } rounded-lg`}>
                              <i className={`${parent.menu.items[0].icon} text-white text-sm`}></i>
                            </div>
                            <div>
                              <p className="text-sm font-bold text-navy-900">{parent.menu.label}</p>
                              <p className="text-xs text-gray-500">
                                {parent.key === 'services' && 'Software dev + talent placement'}
                                {parent.key === 'tech' && 'Explore our tech expertise'}
                                {parent.key === 'industries' && 'Domain-specialized engineers by sector'}
                                {parent.key === 'company' && 'Who we are & how we work'}
                              </p>
                            </div>
                          </div>
                          <div className={`grid ${parent.gridCols} gap-1`}>
                            {parent.menu.items.map((item) => (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setActiveMegaMenu(null)}
                                className={`flex items-start gap-3 px-3 py-3 rounded-xl text-left transition-all ${
                                  isActive(item.to)
                                    ? 'bg-primary-50 text-primary-700'
                                    : 'hover:bg-gray-50 text-navy-900'
                                }`}
                              >
                                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0 mt-0.5">
                                  <i className={`${item.icon} ${
                                    parent.accent === 'emerald' ? 'text-emerald-600' : parent.accent === 'amber' ? 'text-amber-600' : 'text-primary-600'
                                  } text-sm`}></i>
                                </div>
                                <div>
                                  <p className="text-sm font-semibold">{item.label}</p>
                                  {'desc' in item && item.desc && (
                                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                                  )}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {parent.key === 'services' && 'Need something custom?'}
                            {parent.key === 'tech' && 'Not sure what stack you need?'}
                            {parent.key === 'industries' && 'Not sure which sector fits?'}
                            {parent.key === 'company' && 'Want to learn more?'}
                          </span>
                          <button
                            onClick={() => { setActiveMegaMenu(null); handleHomeCTA(); }}
                            className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                          >
                            {parent.key === 'company' ? 'Contact us' : 'Talk to us'}
                            <i className="ri-arrow-right-line inline-block ml-0.5"></i>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {/* Direct links */}
              {directLinks.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-lg ${
                    isActive(item.to)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-navy-900 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Right side: CTAs */}
            <div className="hidden xl:flex items-center space-x-3">
              <button
                onClick={handleHomeCTA}
                className="px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                Schedule a Call
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden w-10 h-10 flex items-center justify-center text-navy-900 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 xl:hidden"
          >
            <div
              className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            ></div>
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute top-0 right-0 bottom-0 w-80 max-w-full bg-white shadow-2xl overflow-y-auto"
            >
              <div className="p-6 pt-24 space-y-1">

                {/* Services expandable section */}
                <div className="border-t border-gray-100 pt-3">
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === 'services' ? null : 'services')}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-navy-900"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-briefcase-line text-primary-600"></i>
                      Services
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileExpanded === 'services' ? 'rotate-180' : ''}`}></i>
                  </button>
                  <AnimatePresence>
                    {mobileExpanded === 'services' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-1 pl-10">
                          {servicesMegaMenu.items.map((item) => {
                            const a = isActive(item.to);
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`block w-full text-left px-3 py-2 text-xs rounded-lg transition-colors ${
                                  a ? 'text-primary-600 bg-primary-50' : 'text-navy-900 hover:bg-gray-50'
                                }`}
                              >
                                {item.label}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Technologies expandable section */}
                <div className="border-t border-gray-100 pt-3">
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === 'tech' ? null : 'tech')}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-navy-900"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-stack-line text-emerald-600"></i>
                      Technologies
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileExpanded === 'tech' ? 'rotate-180' : ''}`}></i>
                  </button>
                  <AnimatePresence>
                    {mobileExpanded === 'tech' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-1 pl-10">
                          {techMegaMenu.items.map((item) => {
                            const a = isActive(item.to);
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`block w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                  a ? 'text-primary-600 bg-primary-50' : 'text-navy-900 hover:bg-gray-50'
                                }`}
                              >
                                {item.label}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Industries expandable section */}
                <div className="border-t border-gray-100 pt-3">
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === 'industries' ? null : 'industries')}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-navy-900"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-building-2-line text-amber-600"></i>
                      Industries
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileExpanded === 'industries' ? 'rotate-180' : ''}`}></i>
                  </button>
                  <AnimatePresence>
                    {mobileExpanded === 'industries' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-1 pl-10">
                          {industriesMegaMenu.items.map((item) => {
                            const a = isActive(item.to);
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`block w-full text-left px-3 py-2 text-xs rounded-lg transition-colors ${
                                  a ? 'text-primary-600 bg-primary-50' : 'text-navy-900 hover:bg-gray-50'
                                }`}
                              >
                                {item.label}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Company expandable section */}
                <div className="border-t border-gray-100 pt-3">
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === 'company' ? null : 'company')}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-navy-900"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-building-4-line text-amber-600"></i>
                      Company
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileExpanded === 'company' ? 'rotate-180' : ''}`}></i>
                  </button>
                  <AnimatePresence>
                    {mobileExpanded === 'company' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-1 pl-10">
                          {companyMenu.items.map((item) => {
                            const a = isActive(item.to);
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`block w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                  a ? 'text-primary-600 bg-primary-50' : 'text-navy-900 hover:bg-gray-50'
                                }`}
                              >
                                {item.label}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>


                {/* Direct links in mobile */}
                {directLinks.map((item) => {
                  const a = isActive(item.to);
                  const iconMap: Record<string, string> = {
                    '/pricing': 'ri-price-tag-3-line',
                    '/careers': 'ri-briefcase-4-line',
                    '/faq': 'ri-questionnaire-line',
                  };
                  return (
                    <div key={item.to} className="border-t border-gray-100 pt-3">
                      <Link
                        to={item.to}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block w-full text-left px-3 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                          a ? 'text-primary-600 bg-primary-50' : 'text-navy-900 hover:bg-gray-50'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className={`${iconMap[item.to] || 'ri-link'} ${a ? 'text-primary-600' : 'text-gray-400'}`}></i>
                          {item.label}
                        </span>
                      </Link>
                    </div>
                  );
                })}

                <div className="pt-4 space-y-3 border-t border-gray-100 mt-3">
                  <button
                    onClick={() => { setIsMobileMenuOpen(false); handleHomeCTA(); }}
                    className="block w-full px-4 py-3 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap text-left"
                  >
                    Schedule a Call
                    <i className="ri-arrow-right-line ml-2 inline-block"></i>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}